import { useState } from "react";
import { useApp, useUi, useUiDispatch } from "@/app/AppContext";
import { useAsync } from "@/components/useAsync";
import {
  customFieldClassName,
  customFieldLabel,
  fetchCustomValues,
  fieldsForLocation,
} from "@/data/customFieldsRepository";
import { useSelectedView } from "@/app/selectors";
import { SimpleMarkdown } from "@/components/SimpleMarkdown";
import { getVisibleExtraSections } from "@/domain/detailSections";
import { localized } from "@/domain/interventionList";
import { hasResponseContent } from "@/domain/questionnaire";
import {
  canClassify,
  canCreateIntervention,
  canEditIntervention,
  canSubmitOwn,
  isRespondentOnly,
  canReadPrivateResponse,
  isPersistedIntervention,
} from "@/domain/permissions";
import type { ExtraSectionStyle, Intervention } from "@/domain/types";
import { getDetailViews, getViewValueStyle } from "@/domain/views";
import { useI18n } from "@/i18n/useI18n";
import { InterventionTabs } from "./InterventionTabs";
import { ResponseSummary } from "./ResponseSummary";
import { UpdatedMeta } from "./UpdatedMeta";
import { InterventionHistory } from "./InterventionHistory";
import { usePrivateResponse } from "./usePrivateResponse";
import { useWorkingComment } from "./useWorkingComment";

const SECTION_CLASS: Record<ExtraSectionStyle, string> = {
  default: "",
  m: " muted",
  h: " highlight",
  t: " text-only",
};

const ICON_PROPS = {
  width: 16,
  height: 16,
  viewBox: "0 0 24 24",
  fill: "none",
  stroke: "currentColor",
  strokeWidth: 2,
  strokeLinecap: "round",
  "aria-hidden": true,
} as const;

interface InterventionDetailsProps {
  item: Intervention;
  /** Respondent list view: no layout toggle; editing opens the editor in the matrix tab. */
  embedded?: boolean;
  onClose(): void;
}

export function InterventionDetails({ item, embedded = false, onClose }: InterventionDetailsProps) {
  const i18n = useI18n();
  const { t, language } = i18n;
  const { authMode, user, data } = useApp();
  const { detailsPosition } = useUi();
  const dispatch = useUiDispatch();
  const [historyInterventionId, setHistoryInterventionId] = useState<string | null>(null);
  const historyOpen = !!item.dbId && historyInterventionId === item.dbId;
  const view = useSelectedView();
  const canReadPrivate = authMode === "individual" && canReadPrivateResponse(user, item);
  const privateResponse = usePrivateResponse(item.dbId, canReadPrivate);
  // Working comments are internal notes for superadmins and local administrators.
  const showComment =
    authMode === "individual" && isPersistedIntervention(item) && canClassify(user);
  const workingComment = useWorkingComment(item.dbId, showComment);
  const customValues = useAsync(item.dbId && authMode === "individual" ? item.dbId : null, () => fetchCustomValues(item.dbId!))[0];

  if (
    canReadPrivate &&
    (privateResponse.status === "loading" || privateResponse.status === "idle")
  ) {
    return <div aria-busy="true">…</div>;
  }

  const currentValue = view.values[item.codes[view.id] ?? ""];
  const moveToRight = detailsPosition !== "right";
  const layoutLabel = moveToRight ? t("moveDetailsRight") : t("moveDetailsBelow");
  const canEdit = authMode === "individual" && canEditIntervention(user, item);
  // Respondents report through the questionnaire, so they start on that tab.
  const isRespondent = authMode === "individual" && canSubmitOwn(user);
  // Respondents (without read access) only see their questionnaire answers.
  const questionnaireOnly = authMode === "individual" && isRespondentOnly(user);
  const edit = () => {
    // The editor lives in the matrix tab; the respondent list only shows details.
    if (embedded) dispatch({ type: "setTab", tab: "matrix" });
    dispatch({ type: "editIntervention", itemId: item.id, editOnly: true });
  };
  const description = localized(item.description, language);
  const status = item.status === "draft" || item.status === "submitted" ? item.status : "approved";
  const statusBadge =
    authMode === "individual" &&
    isPersistedIntervention(item) &&
    canCreateIntervention(user) && (
      <span className={`status-badge ${status}`}>
        {
          {
            draft: t("statusDraft"),
            submitted: t("statusSubmitted"),
            approved: t("statusApproved"),
          }[status]
        }
      </span>
    );

  const facts = [
    { label: t("location"), value: item.region },
    { label: t("arena"), value: item.arena && i18n.arenaLabel(item.arena) },
    { label: t("detailsAge"), value: formatAgeRange(item, t("detailsYears")) },
  ].filter((fact) => fact.value);

  const publicContent = (
    <div className="intervention-details-body">
      {(facts.length > 0 || statusBadge) && (
        <dl className="intervention-details-facts">
          {facts.map((fact) => (
            <div key={fact.label}>
              <dt>{fact.label}</dt>
              <dd>{fact.value}</dd>
            </div>
          ))}
          {statusBadge && (
            <div className="intervention-details-status">
              <dt>{t("listStatus")}</dt>
              <dd>{statusBadge}</dd>
            </div>
          )}
        </dl>
      )}
      <section className="intervention-details-section">
        <h3>{t("listDescription")}</h3>
        {description ? (
          <div className="intervention-details-description">
            <SimpleMarkdown text={description} />
          </div>
        ) : (
          <p className="intervention-details-empty">{t("detailsNoDescription")}</p>
        )}
      </section>
      <ExtraSections item={item} />
      {authMode === "individual" && customValues.status === "ready" &&
        fieldsForLocation(data.settings.customFields, item.region).map((field) => {
          const value = customValues.value[field.id];
          return value ? <section className={["intervention-details-section", "custom-field", customFieldClassName(field)].filter(Boolean).join(" ")} key={field.id}>
            <h3>{customFieldLabel(field, language, data.settings.dictionary)}</h3>
            <SimpleMarkdown text={value} />
          </section> : null;
        })}
      <section className="intervention-details-section">
        <h3>{t("classificationHeading")}</h3>
        <dl className="intervention-details-classifications">
          {getDetailViews(data.settings.views, view).map((detailView) => {
            const code = item.codes[detailView.id] ?? "";
            const value = detailView.values[code];
            return (
              <div key={detailView.id}>
                <dt>{i18n.viewLabel(detailView)}</dt>
                {code ? (
                  <dd>
                    {value && <span className="indicator" style={getViewValueStyle(value)} />}
                    {value
                      ? i18n.viewValueLabel(detailView, code)
                      : item.raw[detailView.id] || code}
                  </dd>
                ) : (
                  <dd className="intervention-details-empty">{t("ikkeAngitt")}</dd>
                )}
              </div>
            );
          })}
        </dl>
      </section>
      {showComment && (
        <section
          className={`intervention-details-section working-comments-view${
            workingComment.status === "ready" && workingComment.value ? " has-comment" : ""
          }`}
        >
          <h3>{t("workingCommentsHeading")}</h3>
          {workingComment.status === "error" ? (
            <p className="auth-gate-message error">{`${t("formLoadError")} ${workingComment.error.message}`}</p>
          ) : workingComment.status === "ready" && workingComment.value ? (
            <p className="working-comments-text">{workingComment.value}</p>
          ) : workingComment.status === "ready" ? (
            <p className="intervention-details-empty">{t("workingCommentsEmpty")}</p>
          ) : null}
        </section>
      )}
      <section className="intervention-details-section">
        <h3>{t("detailsSources")}</h3>
        {item.links.length ? (
          <ol className="intervention-details-links">
            {item.links.map((url, index) => (
              <li key={`${index}:${url}`}>
                <span className="source-link-number">{index + 1}</span>
                <a href={url} target="_blank" rel="noopener noreferrer">
                  {url}
                </a>
              </li>
            ))}
          </ol>
        ) : (
          <p className="intervention-details-empty">{t("detailsNoSources")}</p>
        )}
      </section>
    </div>
  );

  // The questionnaire tab is hidden without answers, unless it is all a respondent sees.
  const response = privateResponse.status === "ready" ? privateResponse.value : null;
  const privateContent = !canReadPrivate ? null : privateResponse.status === "error" ? (
    <p className="auth-gate-message error">{`${t("formLoadError")} ${privateResponse.error.message}`}</p>
  ) : questionnaireOnly || hasResponseContent(response, language) ? (
    <div className="intervention-details-private">
      <ResponseSummary response={response} />
    </div>
  ) : null;

  return (
    <article className="details-card intervention-details">
      <header className="intervention-details-header">
        <div className="intervention-details-title">
          {item.itemNumber && (
            <span
              className={`item details-id-badge${currentValue ? "" : " item-unclassified"}`}
              style={getViewValueStyle(currentValue)}
            >
              {item.itemNumber}
            </span>
          )}
          <h2>{localized(item.display, language)}</h2>
        </div>
        <div className="intervention-details-actions">
          {!embedded && (
            <button
              type="button"
              aria-label={layoutLabel}
              title={layoutLabel}
              onClick={() => dispatch({ type: "toggleDetailsPosition" })}
            >
              <svg {...ICON_PROPS}>
                <rect x="3" y="4" width="18" height="16" rx="2" />
                <path d={moveToRight ? "M14 4v16" : "M3 14h18"} />
              </svg>
            </button>
          )}
          <button type="button" aria-label={t("closeDetails")} onClick={onClose}>
            <svg {...ICON_PROPS}>
              <path d="M6 6l12 12M18 6L6 18" />
            </svg>
          </button>
        </div>
      </header>
      <InterventionTabs
        publicContent={publicContent}
        privateContent={privateContent}
        privateFirst={isRespondent}
        hidePublic={questionnaireOnly}
        showLoneTab
        // Respondents only see the questionnaire tab, so their status sits in the tab row.
        aside={questionnaireOnly ? statusBadge : undefined}
      />
      {authMode === "individual" && isPersistedIntervention(item) && (
        <>
          <footer className="intervention-details-footer">
            <UpdatedMeta
              item={item}
              history={{
                open: historyOpen,
                onToggle: () =>
                  setHistoryInterventionId((current) =>
                    current === item.dbId ? null : (item.dbId ?? null),
                  ),
              }}
            />
            {canEdit && (
              <button type="button" className="details-save-btn" onClick={edit}>
                <svg {...ICON_PROPS} width={15} height={15} strokeLinejoin="round">
                  <path d="M4 20h4L19 9l-4-4L4 16z" />
                </svg>
                {t("listEdit")}
              </button>
            )}
          </footer>
          {historyOpen && canClassify(user) && item.dbId && (
            <div className="intervention-details-history">
              <InterventionHistory interventionId={item.dbId} />
            </div>
          )}
        </>
      )}
    </article>
  );
}

/** "13–16 years", "13+ years" or "0–16 years"; empty without ages. */
function formatAgeRange(item: Intervention, years: string): string {
  const { youngest, oldest } = item;
  if (youngest == null && oldest == null) return "";
  if (oldest == null) return `${youngest}+ ${years}`;
  return `${youngest ?? 0}–${oldest} ${years}`;
}

function ExtraSections({ item }: { item: Intervention }) {
  const { language } = useI18n();
  const sections = getVisibleExtraSections(item.extraSections, language);
  if (!sections.length) return null;
  return (
    <div className="details-extra-box">
      {sections.map((section, index) => (
        <div
          key={`${index}:${section.title}`}
          className={`details-extra-section${SECTION_CLASS[section.style]}`}
        >
          <div className="details-extra-title">{section.title}</div>
          <div className="details-extra-body">
            <SimpleMarkdown text={section.text} />
          </div>
        </div>
      ))}
    </div>
  );
}
